import { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { layoutFlowchart, layoutSequence, layoutClassDiagram, layoutGantt, layoutKanban, layoutQuadrant, layoutRequirements, layoutJourney, layoutPie, layoutMindmap, layoutSankey, layoutTimeline, layoutState, layoutER, layoutBlock, layoutC4, layoutGit, layoutPacket, layoutAIAgent, layoutAgentWorkflow, layoutDAG, layoutPipeline, layoutKnowledgeGraph, layoutModelArch, layoutSystemArch } from "@/lib/diagram-layout";

export default function DiagramRenderer({ graph, nodeColors = {}, annotations = [], onNodeClick, readonly = false }) {
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0 });
  const containerRef = useRef(null);

  const layout = useMemo(() => {
    if (!graph) return null;
    try {
      switch (graph.type) {
        case "ai-agent": return layoutAIAgent(graph);
        case "agent-workflow": return layoutAgentWorkflow(graph);
        case "block": return layoutBlock(graph);
        case "c4": return layoutC4(graph);
        case "class": return layoutClassDiagram(graph);
        case "dag": return layoutDAG(graph);
        case "er": return layoutER(graph);
        case "gantt": return layoutGantt(graph);
        case "git": return layoutGit(graph);
        case "journey": return layoutJourney(graph);
        case "kanban": return layoutKanban(graph);
        case "knowledge-graph": return layoutKnowledgeGraph(graph);
        case "mindmap": return layoutMindmap(graph);
        case "model-arch": return layoutModelArch(graph);
        case "packet": return layoutPacket(graph);
        case "pie": return layoutPie(graph);
        case "pipeline": return layoutPipeline(graph);
        case "quadrant": return layoutQuadrant(graph);
        case "requirements": return layoutRequirements(graph);
        case "sankey": return layoutSankey(graph);
        case "sequence": return layoutSequence(graph);
        case "state": return layoutState(graph);
        case "system-arch": return layoutSystemArch(graph);
        case "timeline": return layoutTimeline(graph);
        default: return layoutFlowchart(graph);
      }
    } catch {
      return null;
    }
  }, [graph]);

  // Center diagram on layout change
  useEffect(() => {
    if (layout && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scaleX = (rect.width - 40) / layout.width;
      const scaleY = (rect.height - 40) / layout.height;
      const scale = Math.min(scaleX, scaleY, 1.2);
      const x = (rect.width - layout.width * scale) / 2;
      const y = (rect.height - layout.height * scale) / 2;
      setTransform({ x: Math.max(0, x), y: Math.max(0, y), scale });
    }
  }, [layout]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.92 : 1.08;
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(3, Math.max(0.1, prev.scale * delta)),
    }));
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return;
      setDragging(true);
      dragRef.current = { startX: e.clientX - transform.x, startY: e.clientY - transform.y };
    },
    [transform.x, transform.y]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!dragging) return;
      setTransform((prev) => ({
        ...prev,
        x: e.clientX - dragRef.current.startX,
        y: e.clientY - dragRef.current.startY,
      }));
    },
    [dragging]
  );

  const handleMouseUp = useCallback(() => setDragging(false), []);

  if (!layout) {
    return (
      <div className="h-full flex items-center justify-center text-slate-400 text-sm font-body" data-testid="empty-preview">
        Start typing DSL code to see the preview
      </div>
    );
  }

  // Find post-it notes anchored to nodes from annotations
  const anchoredNotes = annotations.filter(a => a.type === "sticky" && a.node_id);

  return (
    <div
      ref={containerRef}
      className="h-full w-full overflow-hidden cursor-grab active:cursor-grabbing select-none relative"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      data-testid="diagram-canvas"
    >
      <div
        style={{
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          transformOrigin: "0 0",
          width: layout.width,
          height: layout.height,
          position: "relative",
        }}
      >
        {renderDiagramType(graph?.type, layout, nodeColors, onNodeClick)}
        {/* Render anchored post-it notes inside the SVG coordinate space */}
        {anchoredNotes.map(note => {
          const node = layout.nodes?.find(n => n.id === note.node_id);
          if (!node) return null;
          return (
            <PostItAnchor key={note.id} note={note} node={node} />
          );
        })}
      </div>
    </div>
  );
}

function PostItAnchor({ note, node }) {
  const noteColors = { "#fef08a": "#eab308", "#fed7aa": "#f97316", "#bbf7d0": "#22c55e", "#bfdbfe": "#3b82f6", "#f5d0fe": "#a855f7", "#fecaca": "#ef4444" };
  const border = noteColors[note.color] || "#eab308";
  const x = (node.x || 0) - 80;
  const y = (node.y || 0) - (node.height || 44) / 2 - 90;
  return (
    <div style={{ position: "absolute", left: x, top: y, width: 160, pointerEvents: "none", zIndex: 50 }}>
      <div style={{ background: note.color || "#fef08a", border: `2px solid ${border}`, borderRadius: 8, padding: "6px 10px", fontSize: 11, fontFamily: "Manrope, sans-serif", color: "#334155", boxShadow: "2px 3px 8px rgba(0,0,0,0.12)", lineHeight: 1.4 }}>
        {note.text}
      </div>
      <div style={{ width: 2, height: 16, background: border, margin: "0 auto" }} />
    </div>
  );
}

function FlowchartSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { nodes, edges, subgraphs, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
        <marker id="arrowhead-dashed" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#94a3b8" />
        </marker>
      </defs>

      {subgraphs?.map((sg) => (
        <g key={sg.id}>
          <rect
            x={sg.x - sg.width / 2 - 12}
            y={sg.y - sg.height / 2 - 28}
            width={sg.width + 24}
            height={sg.height + 40}
            rx="6"
            fill="#f8fafc"
            stroke="#e2e8f0"
            strokeWidth="1"
            strokeDasharray="6 3"
          />
          <text
            x={sg.x - sg.width / 2 - 4}
            y={sg.y - sg.height / 2 - 12}
            fill="#64748b"
            fontSize="12"
            fontFamily="Manrope, sans-serif"
            fontWeight="500"
          >
            {sg.label}
          </text>
        </g>
      ))}

      {edges.map((edge, i) => (
        <FlowchartEdge key={i} edge={edge} />
      ))}

      {nodes.map((node) => (
        <FlowchartNode key={node.id} node={node} color={nodeColors[node.id]} onClick={onNodeClick} />
      ))}
    </svg>
  );
}

function FlowchartEdge({ edge }) {
  const { points, label, style } = edge;
  if (!points || points.length < 2) return null;

  const d = points.reduce(
    (acc, p, i) => acc + (i === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`),
    ""
  );

  const midIdx = Math.floor(points.length / 2);
  const midPoint = points[midIdx];

  return (
    <g>
      <path
        d={d}
        fill="none"
        stroke={style === "dashed" ? "#94a3b8" : "#64748b"}
        strokeWidth="1.5"
        strokeDasharray={style === "dashed" ? "6 3" : "none"}
        markerEnd={style === "dashed" ? "url(#arrowhead-dashed)" : "url(#arrowhead)"}
      />
      {label && (
        <g>
          <rect
            x={midPoint.x - label.length * 3.5 - 6}
            y={midPoint.y - 18}
            width={label.length * 7 + 12}
            height={18}
            rx="3"
            fill="white"
            stroke="#e2e8f0"
            strokeWidth="0.5"
          />
          <text
            x={midPoint.x}
            y={midPoint.y - 6}
            textAnchor="middle"
            fill="#64748b"
            fontSize="11"
            fontFamily="Manrope, sans-serif"
          >
            {label}
          </text>
        </g>
      )}
    </g>
  );
}

function FlowchartNode({ node, color, onClick }) {
  const { x, y, width, height, label, shape, id } = node;
  const fillColor = color && color !== "transparent" ? color : "white";
  const hasCustomColor = color && color !== "transparent";

  const handleClick = (e) => {
    if (onClick) {
      e.stopPropagation();
      const rect = e.currentTarget.closest("svg").getBoundingClientRect();
      onClick(id, e.clientX, e.clientY);
    }
  };

  const textEl = (
    <text
      x={x}
      y={y + 4}
      textAnchor="middle"
      fill={hasCustomColor ? "#fff" : "#0f172a"}
      fontSize="13"
      fontFamily="Manrope, sans-serif"
      fontWeight="500"
      style={{ pointerEvents: "none" }}
    >
      {label}
    </text>
  );

  const nodeStyle = onClick ? { cursor: "pointer" } : {};

  switch (shape) {
    case "diamond":
      return (
        <g data-testid={`node-${id}`} className="diagram-node" onClick={handleClick} style={nodeStyle}>
          <polygon
            points={`${x},${y - height / 2} ${x + width / 2},${y} ${x},${y + height / 2} ${x - width / 2},${y}`}
            fill={fillColor}
            stroke={hasCustomColor ? color : "#0f172a"}
            strokeWidth="1.5"
          />
          {textEl}
        </g>
      );

    case "circle": {
      const r = Math.max(width, height) / 2;
      return (
        <g data-testid={`node-${id}`} className="diagram-node" onClick={handleClick} style={nodeStyle}>
          <circle cx={x} cy={y} r={r} fill={fillColor} stroke={hasCustomColor ? color : "#0f172a"} strokeWidth="1.5" />
          {textEl}
        </g>
      );
    }

    case "database": {
      const rx = width / 2;
      const ry = 8;
      const top = y - height / 2;
      const bottom = y + height / 2;
      return (
        <g data-testid={`node-${id}`} className="diagram-node" onClick={handleClick} style={nodeStyle}>
          <path
            d={`M ${x - rx} ${top + ry} L ${x - rx} ${bottom - ry} A ${rx} ${ry} 0 0 0 ${x + rx} ${bottom - ry} L ${x + rx} ${top + ry}`}
            fill={fillColor}
            stroke={hasCustomColor ? color : "#0f172a"}
            strokeWidth="1.5"
          />
          <ellipse cx={x} cy={top + ry} rx={rx} ry={ry} fill={fillColor} stroke={hasCustomColor ? color : "#0f172a"} strokeWidth="1.5" />
          {textEl}
        </g>
      );
    }

    case "rounded":
      return (
        <g data-testid={`node-${id}`} className="diagram-node" onClick={handleClick} style={nodeStyle}>
          <rect
            x={x - width / 2}
            y={y - height / 2}
            width={width}
            height={height}
            rx={20}
            fill={fillColor}
            stroke={hasCustomColor ? color : "#0f172a"}
            strokeWidth="1.5"
          />
          {textEl}
        </g>
      );

    default:
      return (
        <g data-testid={`node-${id}`} className="diagram-node" onClick={handleClick} style={nodeStyle}>
          <rect
            x={x - width / 2}
            y={y - height / 2}
            width={width}
            height={height}
            rx={4}
            fill={fillColor}
            stroke={hasCustomColor ? color : "#0f172a"}
            strokeWidth="1.5"
          />
          {textEl}
        </g>
      );
  }
}

function SequenceSVG({ layout }) {
  const { actors, messages, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="seq-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#0f172a" />
        </marker>
        <marker id="seq-arrow-reply" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
        </marker>
      </defs>

      {actors.map((actor) => (
        <g key={actor.id}>
          <rect
            x={actor.x - 60}
            y={actor.y - 20}
            width={120}
            height={40}
            rx={4}
            fill="white"
            stroke="#0f172a"
            strokeWidth="1.5"
          />
          <text
            x={actor.x}
            y={actor.y + 5}
            textAnchor="middle"
            fill="#0f172a"
            fontSize="13"
            fontFamily="Manrope, sans-serif"
            fontWeight="500"
          >
            {actor.label}
          </text>
          <line
            x1={actor.x}
            y1={actor.y + 20}
            x2={actor.x}
            y2={height - 20}
            stroke="#e2e8f0"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
        </g>
      ))}

      {messages.map((msg, i) => (
        <g key={i}>
          <line
            x1={msg.fromX}
            y1={msg.y}
            x2={msg.toX}
            y2={msg.y}
            stroke={msg.type === "reply" ? "#94a3b8" : "#0f172a"}
            strokeWidth="1.5"
            strokeDasharray={msg.type === "reply" ? "6 3" : "none"}
            markerEnd={msg.type === "reply" ? "url(#seq-arrow-reply)" : "url(#seq-arrow)"}
          />
          <text
            x={(msg.fromX + msg.toX) / 2}
            y={msg.y - 10}
            textAnchor="middle"
            fill="#334155"
            fontSize="12"
            fontFamily="Manrope, sans-serif"
          >
            {msg.message}
          </text>
        </g>
      ))}
    </svg>
  );
}

function renderDiagramType(type, layout, nodeColors = {}, onNodeClick) {
  switch (type) {
    case "ai-agent": return <AIAgentSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "agent-workflow": return <AgentWorkflowSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "block": return <BlockSVG layout={layout} />;
    case "c4": return <C4SVG layout={layout} />;
    case "class": return <ClassDiagramSVG layout={layout} />;
    case "dag": return <DAGSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "er": return <ERSVG layout={layout} />;
    case "gantt": return <GanttSVG layout={layout} />;
    case "git": return <GitSVG layout={layout} />;
    case "journey": return <JourneySVG layout={layout} />;
    case "kanban": return <KanbanSVG layout={layout} />;
    case "knowledge-graph": return <KnowledgeGraphSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "mindmap": return <MindmapSVG layout={layout} />;
    case "model-arch": return <ModelArchSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "packet": return <PacketSVG layout={layout} />;
    case "pie": return <PieSVG layout={layout} />;
    case "pipeline": return <PipelineSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "quadrant": return <QuadrantSVG layout={layout} />;
    case "requirements": return <RequirementsSVG layout={layout} />;
    case "sankey": return <SankeySVG layout={layout} />;
    case "sequence": return <SequenceSVG layout={layout} />;
    case "state": return <StateSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "system-arch": return <SystemArchSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
    case "timeline": return <TimelineSVG layout={layout} />;
    default: return <FlowchartSVG layout={layout} nodeColors={nodeColors} onNodeClick={onNodeClick} />;
  }
}

function ClassDiagramSVG({ layout }) {
  const { classes, relationships, width, height } = layout;
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="inherit-arrow" markerWidth="14" markerHeight="10" refX="14" refY="5" orient="auto">
          <polygon points="0 0, 14 5, 0 10" fill="white" stroke="#0f172a" strokeWidth="1.5" />
        </marker>
        <marker id="compose-arrow" markerWidth="12" markerHeight="12" refX="12" refY="6" orient="auto">
          <polygon points="0 6, 6 0, 12 6, 6 12" fill="#0f172a" />
        </marker>
        <marker id="aggregate-arrow" markerWidth="12" markerHeight="12" refX="12" refY="6" orient="auto">
          <polygon points="0 6, 6 0, 12 6, 6 12" fill="white" stroke="#0f172a" strokeWidth="1.5" />
        </marker>
        <marker id="class-arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      {relationships.map((rel, i) => {
        if (!rel.points || rel.points.length < 2) return null;
        const d = rel.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        const marker = rel.type === "<|--" ? "url(#inherit-arrow)" : rel.type === "*--" ? "url(#compose-arrow)" : rel.type === "o--" ? "url(#aggregate-arrow)" : "url(#class-arrow)";
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#64748b" strokeWidth="1.5" markerEnd={marker} strokeDasharray={rel.type === "..>" ? "6 3" : "none"} />
            {rel.label && <text x={rel.points[Math.floor(rel.points.length / 2)].x + 8} y={rel.points[Math.floor(rel.points.length / 2)].y - 6} fill="#94a3b8" fontSize="10" fontFamily="Manrope, sans-serif">{rel.label}</text>}
          </g>
        );
      })}
      {classes.map((cls) => {
        const { x, y, width: w, height: h, name, properties, methods, headerH, lineHeight } = cls;
        const top = y - h / 2;
        return (
          <g key={name} data-testid={`class-${name}`}>
            <rect x={x - w / 2} y={top} width={w} height={h} rx={4} fill="white" stroke="#0f172a" strokeWidth="1.5" />
            <rect x={x - w / 2} y={top} width={w} height={headerH} rx={4} fill="#f1f5f9" stroke="none" />
            <line x1={x - w / 2} y1={top + headerH} x2={x + w / 2} y2={top + headerH} stroke="#e2e8f0" strokeWidth="1" />
            <text x={x} y={top + headerH / 2 + 5} textAnchor="middle" fill="#0f172a" fontSize="14" fontWeight="600" fontFamily="Manrope, sans-serif">{name}</text>
            {properties.map((p, i) => (
              <text key={`p${i}`} x={x - w / 2 + 12} y={top + headerH + 18 + i * lineHeight} fill="#334155" fontSize="12" fontFamily="JetBrains Mono, monospace">
                {p.visibility}{p.name}{p.type ? `: ${p.type}` : ""}
              </text>
            ))}
            {methods.length > 0 && (
              <line x1={x - w / 2} y1={top + headerH + properties.length * lineHeight + 10} x2={x + w / 2} y2={top + headerH + properties.length * lineHeight + 10} stroke="#e2e8f0" strokeWidth="1" />
            )}
            {methods.map((m, i) => (
              <text key={`m${i}`} x={x - w / 2 + 12} y={top + headerH + properties.length * lineHeight + 28 + i * lineHeight} fill="#334155" fontSize="12" fontFamily="JetBrains Mono, monospace">
                {m.visibility}{m.name}({m.params}){m.returnType ? `: ${m.returnType}` : ""}
              </text>
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function GanttSVG({ layout }) {
  const { title, sections, width, height, dayWidth, leftMargin, topMargin, totalDays, startDate } = layout;
  if (!startDate) return <svg id="diagram-svg" width={600} height={200}><text x={300} y={100} textAnchor="middle" fill="#94a3b8" fontSize="14" fontFamily="Manrope">No tasks defined</text></svg>;
  const colors = ["#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit, sans-serif">{title}</text>
      {Array.from({ length: totalDays }, (_, i) => {
        const d = new Date(startDate);
        d.setDate(d.getDate() + i);
        return (
          <g key={i}>
            <line x1={leftMargin + i * dayWidth} y1={topMargin - 5} x2={leftMargin + i * dayWidth} y2={height} stroke="#f1f5f9" strokeWidth="1" />
            {i % 5 === 0 && <text x={leftMargin + i * dayWidth} y={topMargin - 12} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="Manrope, sans-serif">{d.toLocaleDateString("en", { month: "short", day: "numeric" })}</text>}
          </g>
        );
      })}
      {sections.map((section, si) => (
        <g key={si}>
          <text x={10} y={section.y + 20} fill="#0f172a" fontSize="12" fontWeight="600" fontFamily="Manrope, sans-serif">{section.name}</text>
          {section.tasks.map((task, ti) => (
            <g key={ti}>
              <text x={10} y={task.y + 20} fill="#64748b" fontSize="11" fontFamily="Manrope, sans-serif">{task.name}</text>
              <rect x={leftMargin + task.startDay * dayWidth} y={task.y + 5} width={Math.max(task.duration * dayWidth - 4, 8)} height={20} rx={4} fill={colors[si % colors.length]} opacity={0.85} />
              <text x={leftMargin + task.startDay * dayWidth + 6} y={task.y + 19} fill="white" fontSize="9" fontWeight="500" fontFamily="Manrope">{task.duration}d</text>
            </g>
          ))}
        </g>
      ))}
    </svg>
  );
}

function KanbanSVG({ layout }) {
  const { columns, width, height, colWidth, cardH, headerH, pad } = layout;
  const colBg = ["#f8fafc", "#eff6ff", "#f0fdf4", "#fefce8", "#fdf2f8"];
  const headerBg = ["#e2e8f0", "#bfdbfe", "#bbf7d0", "#fde68a", "#fbcfe8"];
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      {columns.map((col, i) => (
        <g key={i} data-testid={`kanban-col-${i}`}>
          <rect x={col.x} y={pad} width={colWidth} height={height - pad * 2} rx={8} fill={colBg[i % colBg.length]} stroke="#e2e8f0" strokeWidth="1" />
          <rect x={col.x} y={pad} width={colWidth} height={headerH} rx={8} fill={headerBg[i % headerBg.length]} />
          <rect x={col.x} y={pad + headerH - 8} width={colWidth} height={8} fill={headerBg[i % headerBg.length]} />
          <text x={col.x + colWidth / 2} y={pad + headerH / 2 + 5} textAnchor="middle" fill="#0f172a" fontSize="13" fontWeight="600" fontFamily="Manrope, sans-serif">{col.name}</text>
          <text x={col.x + colWidth - 14} y={pad + headerH / 2 + 4} textAnchor="end" fill="#94a3b8" fontSize="11">{col.items.length}</text>
          {col.items.map((item, j) => (
            <g key={j}>
              <rect x={col.x + 8} y={pad + item.y + 8} width={colWidth - 16} height={cardH - 4} rx={6} fill="white" stroke="#e2e8f0" strokeWidth="1" />
              <text x={col.x + 18} y={pad + item.y + cardH / 2 + 10} fill="#334155" fontSize="12" fontFamily="Manrope, sans-serif">
                {item.text.length > 24 ? item.text.substring(0, 24) + "..." : item.text}
              </text>
            </g>
          ))}
        </g>
      ))}
    </svg>
  );
}

function QuadrantSVG({ layout }) {
  const { title, xAxis, yAxis, quadrantLabels, items, size, margin, width, height } = layout;
  const qBg = ["#eff6ff", "#f0fdf4", "#fef2f2", "#fefce8"];
  const dotC = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#f97316", "#ec4899"];
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={35} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit, sans-serif">{title}</text>
      <rect x={margin} y={margin} width={size / 2} height={size / 2} fill={qBg[0]} opacity={0.5} />
      <rect x={margin + size / 2} y={margin} width={size / 2} height={size / 2} fill={qBg[1]} opacity={0.5} />
      <rect x={margin} y={margin + size / 2} width={size / 2} height={size / 2} fill={qBg[2]} opacity={0.5} />
      <rect x={margin + size / 2} y={margin + size / 2} width={size / 2} height={size / 2} fill={qBg[3]} opacity={0.5} />
      {quadrantLabels[0] && <text x={margin + size * 0.25} y={margin + 20} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="500" fontFamily="Manrope">{quadrantLabels[0]}</text>}
      {quadrantLabels[1] && <text x={margin + size * 0.75} y={margin + 20} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="500" fontFamily="Manrope">{quadrantLabels[1]}</text>}
      {quadrantLabels[2] && <text x={margin + size * 0.25} y={margin + size - 10} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="500" fontFamily="Manrope">{quadrantLabels[2]}</text>}
      {quadrantLabels[3] && <text x={margin + size * 0.75} y={margin + size - 10} textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="500" fontFamily="Manrope">{quadrantLabels[3]}</text>}
      <line x1={margin} y1={margin + size / 2} x2={margin + size} y2={margin + size / 2} stroke="#cbd5e1" strokeWidth="1.5" />
      <line x1={margin + size / 2} y1={margin} x2={margin + size / 2} y2={margin + size} stroke="#cbd5e1" strokeWidth="1.5" />
      <rect x={margin} y={margin} width={size} height={size} fill="none" stroke="#e2e8f0" strokeWidth="1.5" rx={4} />
      <text x={margin} y={margin + size + 20} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{xAxis.left}</text>
      <text x={margin + size} y={margin + size + 20} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="Manrope">{xAxis.right}</text>
      <text x={margin - 10} y={margin + size} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="Manrope">{yAxis.bottom}</text>
      <text x={margin - 10} y={margin + 10} textAnchor="end" fill="#94a3b8" fontSize="10" fontFamily="Manrope">{yAxis.top}</text>
      {items.map((item, i) => {
        const ix = margin + item.x * size;
        const iy = margin + (1 - item.y) * size;
        return (
          <g key={i} data-testid={`quadrant-item-${i}`}>
            <circle cx={ix} cy={iy} r={7} fill={dotC[i % dotC.length]} />
            <text x={ix + 12} y={iy + 4} fill="#334155" fontSize="11" fontFamily="Manrope, sans-serif">{item.name}</text>
          </g>
        );
      })}
    </svg>
  );
}

function RequirementsSVG({ layout }) {
  const { requirements, relationships, width, height } = layout;
  const prioC = { high: "#ef4444", medium: "#f59e0b", low: "#10b981" };
  const statC = { approved: "#10b981", pending: "#f59e0b", planned: "#2563eb", rejected: "#ef4444" };
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="req-arr" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      {relationships.map((rel, i) => {
        if (!rel.points || rel.points.length < 2) return null;
        const d = rel.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        const mid = rel.points[Math.floor(rel.points.length / 2)];
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#req-arr)" />
            {rel.type && <text x={mid.x + 8} y={mid.y - 6} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{rel.type}</text>}
          </g>
        );
      })}
      {requirements.map((req) => {
        if (!req.x) return null;
        const { x, y, width: w, height: h, name, id, priority, status } = req;
        return (
          <g key={id} data-testid={`req-${id}`}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={4} fill="white" stroke="#0f172a" strokeWidth="1.5" />
            <text x={x} y={y - 16} textAnchor="middle" fill="#0f172a" fontSize="12" fontWeight="600" fontFamily="Manrope">{name}</text>
            <text x={x} y={y} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="JetBrains Mono, monospace">{id}</text>
            {priority && <rect x={x - 35} y={y + 10} width={30} height={15} rx={3} fill={prioC[priority] || "#94a3b8"} opacity={0.15} />}
            {priority && <text x={x - 20} y={y + 21} textAnchor="middle" fill={prioC[priority] || "#94a3b8"} fontSize="8" fontWeight="600" fontFamily="Manrope">{priority}</text>}
            {status && <rect x={x + 5} y={y + 10} width={36} height={15} rx={3} fill={statC[status] || "#94a3b8"} opacity={0.15} />}
            {status && <text x={x + 23} y={y + 21} textAnchor="middle" fill={statC[status] || "#94a3b8"} fontSize="8" fontWeight="600" fontFamily="Manrope">{status}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function JourneySVG({ layout }) {
  const { title, sections, width, height, stepW, topM } = layout;
  const secC = ["#2563eb", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
  const scoreC = ["", "#ef4444", "#f59e0b", "#eab308", "#22c55e", "#10b981"];
  const scoreBg = ["", "#fef2f2", "#fef2f2", "#fefce8", "#f0fdf4", "#f0fdf4"];
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit, sans-serif">{title}</text>
      <line x1={40} y1={topM + 60} x2={width - 40} y2={topM + 60} stroke="#e2e8f0" strokeWidth="2" />
      {sections.map((section, si) => {
        const color = secC[si % secC.length];
        return (
          <g key={si}>
            <rect x={section.x} y={topM} width={section.width} height={110} rx={8} fill={color} opacity={0.06} />
            <text x={section.x + section.width / 2} y={topM + 18} textAnchor="middle" fill={color} fontSize="12" fontWeight="600" fontFamily="Manrope">{section.name}</text>
            {section.steps.map((step, i) => {
              const cx = step.x + stepW / 2;
              const cy = topM + 60;
              return (
                <g key={i}>
                  <circle cx={cx} cy={cy} r={14} fill={scoreBg[step.score] || "#f8fafc"} stroke={scoreC[step.score] || "#94a3b8"} strokeWidth="2" />
                  <text x={cx} y={cy + 4} textAnchor="middle" fill={scoreC[step.score] || "#94a3b8"} fontSize="12" fontWeight="700" fontFamily="Manrope">{step.score}</text>
                  <text x={cx} y={cy + 34} textAnchor="middle" fill="#334155" fontSize="11" fontFamily="Manrope">{step.name}</text>
                  <text x={cx} y={cy + 48} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="Manrope">{step.actor}</text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

// --- NEW DIAGRAM TYPES ---

const COLORS = ["#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#84cc16"];

function PieSVG({ layout }) {
  const { title, slices, centerX, centerY, radius, width, height } = layout;
  
  const describeArc = (cx, cy, r, startAngle, endAngle) => {
    const start = { x: cx + r * Math.cos(startAngle), y: cy + r * Math.sin(startAngle) };
    const end = { x: cx + r * Math.cos(endAngle), y: cy + r * Math.sin(endAngle) };
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
  };

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={centerX} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>
      {slices.map((slice, i) => (
        <g key={i}>
          <path d={describeArc(centerX, centerY, radius, slice.startAngle, slice.endAngle)} fill={COLORS[i % COLORS.length]} opacity={0.85} />
          <text
            x={centerX + (radius * 0.65) * Math.cos(slice.midAngle)}
            y={centerY + (radius * 0.65) * Math.sin(slice.midAngle)}
            textAnchor="middle" fill="white" fontSize="11" fontWeight="600" fontFamily="Manrope"
          >{slice.percentage}%</text>
        </g>
      ))}
      <g transform={`translate(${width - 120}, 60)`}>
        {slices.map((slice, i) => (
          <g key={i} transform={`translate(0, ${i * 22})`}>
            <rect width={14} height={14} rx={3} fill={COLORS[i % COLORS.length]} />
            <text x={20} y={11} fill="#334155" fontSize="11" fontFamily="Manrope">{slice.label}</text>
          </g>
        ))}
      </g>
    </svg>
  );
}

function MindmapSVG({ layout }) {
  const { root, width, height } = layout;

  const renderNode = (node, parentX, parentY) => (
    <g key={node.label + node.x}>
      {parentX !== null && (
        <path d={`M ${parentX} ${parentY} C ${(parentX + node.x) / 2} ${parentY}, ${(parentX + node.x) / 2} ${node.y}, ${node.x} ${node.y}`}
          fill="none" stroke="#cbd5e1" strokeWidth="2" />
      )}
      <rect x={node.x - node.width / 2} y={node.y - node.height / 2} width={node.width} height={node.height} rx={18} fill={parentX === null ? "#3b82f6" : "#f1f5f9"} stroke={parentX === null ? "#2563eb" : "#e2e8f0"} strokeWidth="2" />
      <text x={node.x} y={node.y + 4} textAnchor="middle" fill={parentX === null ? "white" : "#334155"} fontSize="12" fontWeight={parentX === null ? "600" : "500"} fontFamily="Manrope">{node.label}</text>
      {(node.children || []).map(child => renderNode(child, node.x + node.width / 2, node.y))}
    </g>
  );

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      {renderNode(root, null, null)}
    </svg>
  );
}

function SankeySVG({ layout }) {
  const { title, nodes, flows, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>
      {flows.map((flow, i) => (
        <path key={i} d={`M ${flow.sourceX} ${flow.sourceY} C ${flow.sourceX + 100} ${flow.sourceY}, ${flow.targetX - 100} ${flow.targetY}, ${flow.targetX} ${flow.targetY}`}
          fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth={Math.max(4, flow.value / 5)} opacity={0.5} />
      ))}
      {nodes.map((node, i) => (
        <g key={i}>
          <rect x={node.x} y={node.y} width={node.width} height={node.height} fill={COLORS[i % COLORS.length]} rx={4} />
          <text x={node.x + node.width + 8} y={node.y + node.height / 2 + 4} fill="#334155" fontSize="11" fontFamily="Manrope">{node.id}</text>
        </g>
      ))}
    </svg>
  );
}

function TimelineSVG({ layout }) {
  const { title, events, width, height, lineY } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>
      <line x1={60} y1={lineY} x2={width - 60} y2={lineY} stroke="#e2e8f0" strokeWidth="3" />
      {events.map((event, i) => (
        <g key={i}>
          <circle cx={event.x} cy={lineY} r={8} fill={COLORS[i % COLORS.length]} />
          <line x1={event.x} y1={lineY + (event.above ? -15 : 15)} x2={event.x} y2={lineY + (event.above ? -50 : 50)} stroke="#cbd5e1" strokeWidth="1" />
          <text x={event.x} y={lineY + (event.above ? -60 : 70)} textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="Manrope">{event.date}</text>
          <text x={event.x} y={lineY + (event.above ? -75 : 85)} textAnchor="middle" fill="#64748b" fontSize="10" fontFamily="Manrope">{event.description.slice(0, 20)}</text>
        </g>
      ))}
    </svg>
  );
}

function StateSVG({ layout }) {
  const { states, transitions, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="state-arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      {transitions.map((t, i) => {
        if (!t.points || t.points.length < 2) return null;
        const d = t.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#64748b" strokeWidth="1.5" markerEnd="url(#state-arrow)" />
            {t.label && <text x={t.points[Math.floor(t.points.length / 2)].x} y={t.points[Math.floor(t.points.length / 2)].y - 8} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{t.label}</text>}
          </g>
        );
      })}
      {states.map((state, i) => (
        state.type === "start" || state.type === "end" ? (
          <circle key={i} cx={state.x} cy={state.y} r={12} fill={state.type === "start" ? "#0f172a" : "white"} stroke="#0f172a" strokeWidth="3" />
        ) : (
          <g key={i}>
            <rect x={state.x - state.width / 2} y={state.y - state.height / 2} width={state.width} height={state.height} rx={12} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
            <text x={state.x} y={state.y + 4} textAnchor="middle" fill="#0f172a" fontSize="12" fontWeight="500" fontFamily="Manrope">{state.label}</text>
          </g>
        )
      ))}
    </svg>
  );
}

function ERSVG({ layout }) {
  const { entities, relationships, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      {relationships.map((rel, i) => (
        <g key={i}>
          <line x1={rel.fromX} y1={rel.fromY} x2={rel.toX} y2={rel.toY} stroke="#64748b" strokeWidth="1.5" />
          <text x={(rel.fromX + rel.toX) / 2} y={(rel.fromY + rel.toY) / 2 - 8} fill="#94a3b8" fontSize="10" textAnchor="middle" fontFamily="Manrope">{rel.label}</text>
        </g>
      ))}
      {entities.map((entity, i) => (
        <g key={i}>
          <rect x={entity.x} y={entity.y} width={entity.width} height={entity.height} rx={4} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
          <rect x={entity.x} y={entity.y} width={entity.width} height={32} rx={4} fill="#3b82f6" />
          <text x={entity.x + entity.width / 2} y={entity.y + 21} textAnchor="middle" fill="white" fontSize="13" fontWeight="600" fontFamily="Outfit">{entity.name}</text>
          {entity.attributes.map((attr, j) => (
            <text key={j} x={entity.x + 12} y={entity.y + 52 + j * 22} fill="#334155" fontSize="11" fontFamily="JetBrains Mono, monospace">
              <tspan fill={attr.key ? "#f59e0b" : "#64748b"}>{attr.key || "  "}</tspan>{" "}
              <tspan fill="#3b82f6">{attr.type}</tspan> {attr.name}
            </text>
          ))}
        </g>
      ))}
    </svg>
  );
}

function BlockSVG({ layout }) {
  const { blocks, width, height } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      {blocks.map((block, i) => (
        <g key={i}>
          <rect x={block.x} y={block.y} width={block.width} height={block.height} rx={8} fill={COLORS[i % COLORS.length]} opacity={0.1} stroke={COLORS[i % COLORS.length]} strokeWidth="2" />
          <text x={block.x + block.width / 2} y={block.y + 24} textAnchor="middle" fill={COLORS[i % COLORS.length]} fontSize="13" fontWeight="600" fontFamily="Outfit">{block.label}</text>
          {block.children.map((child, j) => (
            <text key={j} x={block.x + 16} y={block.y + 50 + j * 18} fill="#64748b" fontSize="11" fontFamily="Manrope">{child}</text>
          ))}
        </g>
      ))}
    </svg>
  );
}

function C4SVG({ layout }) {
  const { title, elements, relationships, width, height } = layout;
  const typeColors = { person: "#3b82f6", system: "#22c55e", container: "#8b5cf6", component: "#f59e0b", database: "#06b6d4" };

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="c4-arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>
      {relationships.map((rel, i) => {
        if (!rel.points || rel.points.length < 2) return null;
        const d = rel.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#64748b" strokeWidth="1.5" strokeDasharray="6 3" markerEnd="url(#c4-arrow)" />
            {rel.label && <text x={rel.points[Math.floor(rel.points.length / 2)].x} y={rel.points[Math.floor(rel.points.length / 2)].y - 8} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{rel.label}</text>}
          </g>
        );
      })}
      {elements.map((elem, i) => (
        <g key={i}>
          {elem.type === "person" ? (
            <>
              <circle cx={elem.x} cy={elem.y - 20} r={20} fill={typeColors[elem.type]} />
              <rect x={elem.x - elem.width / 2} y={elem.y} width={elem.width} height={elem.height - 20} rx={8} fill={typeColors[elem.type]} />
            </>
          ) : (
            <rect x={elem.x - elem.width / 2} y={elem.y - elem.height / 2} width={elem.width} height={elem.height} rx={8} fill={typeColors[elem.type] || "#94a3b8"} />
          )}
          <text x={elem.x} y={elem.type === "person" ? elem.y + 30 : elem.y} textAnchor="middle" fill="white" fontSize="12" fontWeight="600" fontFamily="Outfit">{elem.label}</text>
          {elem.description && <text x={elem.x} y={elem.type === "person" ? elem.y + 46 : elem.y + 16} textAnchor="middle" fill="rgba(255,255,255,0.8)" fontSize="9" fontFamily="Manrope">{elem.description}</text>}
        </g>
      ))}
    </svg>
  );
}

function GitSVG({ layout }) {
  const { branches, commits, width, height } = layout;
  const branchColors = { main: "#22c55e", develop: "#3b82f6", feature: "#8b5cf6", hotfix: "#ef4444" };

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      {branches.map((branch, i) => (
        <g key={i}>
          <line x1={60} y1={branch.y} x2={width - 40} y2={branch.y} stroke={branchColors[branch.name] || COLORS[i % COLORS.length]} strokeWidth="3" opacity={0.3} />
          <text x={20} y={branch.y + 4} fill="#64748b" fontSize="11" fontFamily="Manrope">{branch.name}</text>
        </g>
      ))}
      {commits.map((commit, i) => {
        const color = branchColors[commit.branch] || COLORS[i % COLORS.length];
        return (
          <g key={i}>
            {i > 0 && commits[i - 1].branch === commit.branch && (
              <line x1={commits[i - 1].x} y1={commit.y} x2={commit.x} y2={commit.y} stroke={color} strokeWidth="3" />
            )}
            <circle cx={commit.x} cy={commit.y} r={commit.merge ? 10 : 8} fill={commit.merge ? "white" : color} stroke={color} strokeWidth="3" />
            <text x={commit.x} y={commit.y - 16} textAnchor="middle" fill="#334155" fontSize="9" fontFamily="Manrope">{commit.message.slice(0, 15)}</text>
          </g>
        );
      })}
    </svg>
  );
}

function PacketSVG({ layout }) {
  const { title, fields, width, height, bitsPerRow, bitWidth, margin } = layout;

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>
      {/* Bit numbers */}
      {Array.from({ length: bitsPerRow }, (_, i) => (
        <text key={i} x={margin + i * bitWidth + bitWidth / 2} y={margin + 28} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="JetBrains Mono">{i}</text>
      ))}
      {fields.map((field, i) => (
        <g key={i}>
          <rect x={field.x} y={field.y} width={field.width} height={field.height} fill={COLORS[i % COLORS.length]} opacity={0.2} stroke={COLORS[i % COLORS.length]} strokeWidth="2" rx={2} />
          <text x={field.x + field.width / 2} y={field.y + field.height / 2 + 4} textAnchor="middle" fill="#334155" fontSize="11" fontWeight="500" fontFamily="Manrope">{field.label}</text>
        </g>
      ))}
    </svg>
  );
}

// --- AI / ML DIAGRAM RENDERERS ---

const AI_COLORS = {
  user: "#3b82f6",
  agent: "#8b5cf6",
  llm: "#22c55e",
  model: "#22c55e",
  tool: "#f59e0b",
  tools: "#f59e0b",
  memory: "#ec4899",
  vectordb: "#06b6d4",
  api: "#ef4444",
  environment: "#64748b",
  decision: "#f59e0b",
  process: "#3b82f6",
  task: "#22c55e",
  start: "#22c55e",
  end: "#ef4444",
  input: "#3b82f6",
  output: "#22c55e",
  dense: "#3b82f6",
  conv: "#8b5cf6",
  pooling: "#06b6d4",
  dropout: "#f59e0b",
  activation: "#ec4899",
  embedding: "#ef4444",
  attention: "#f97316",
};

function EdgeArrowDefs() {
  return (
    <defs>
      <marker id="ai-arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
      <marker id="ai-arrow-bi" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
        <polygon points="10 0, 0 3.5, 10 7" fill="#64748b" />
      </marker>
    </defs>
  );
}

function AIEdges({ connections, marker = "url(#ai-arrow)" }) {
  return connections.map((conn, i) => {
    if (!conn.points || conn.points.length < 2) return null;
    const d = conn.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
    const mid = conn.points[Math.floor(conn.points.length / 2)];
    return (
      <g key={i}>
        <path d={d} fill="none" stroke="#64748b" strokeWidth="1.5"
          markerEnd={marker}
          markerStart={conn.bidirectional ? "url(#ai-arrow-bi)" : "none"}
        />
        {conn.label && (
          <text x={mid.x + 6} y={mid.y - 6} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{conn.label}</text>
        )}
      </g>
    );
  });
}

function AIAgentSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, components, connections, width, height } = layout;
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <EdgeArrowDefs />
      {title && <text x={width / 2} y={28} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      <AIEdges connections={connections} />
      {components.map((comp) => {
        const defaultColor = AI_COLORS[comp.type] || "#94a3b8";
        const customColor = nodeColors[comp.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label, description } = comp;
        return (
          <g key={comp.id} data-testid={`ai-agent-${comp.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(comp.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={8} fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
            <text x={x} y={description ? y - 6 : y + 4} textAnchor="middle" fill="#0f172a" fontSize="12" fontWeight="600" fontFamily="Manrope">{label}</text>
            {description && <text x={x} y={y + 10} textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Manrope">{description}</text>}
            <rect x={x - w / 2} y={y - h / 2} width={28} height={16} rx={4} fill={color} />
            <text x={x - w / 2 + 14} y={y - h / 2 + 11} textAnchor="middle" fill="white" fontSize="8" fontWeight="700" fontFamily="Manrope">{comp.type?.slice(0, 4).toUpperCase()}</text>
          </g>
        );
      })}
    </svg>
  );
}

function AgentWorkflowSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, nodes, edges, width, height } = layout;
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <EdgeArrowDefs />
      {title && <text x={width / 2} y={28} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {edges.map((edge, i) => {
        if (!edge.points || edge.points.length < 2) return null;
        const d = edge.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        const mid = edge.points[Math.floor(edge.points.length / 2)];
        return (
          <g key={i}>
            <path d={d} fill="none" stroke={edge.type === "loop" ? "#f59e0b" : "#64748b"} strokeWidth="1.5"
              strokeDasharray={edge.type === "loop" ? "6 3" : "none"}
              markerEnd="url(#ai-arrow)" />
            {edge.label && <text x={mid.x + 6} y={mid.y - 6} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{edge.label}</text>}
          </g>
        );
      })}
      {nodes.map((node) => {
        const defaultColor = AI_COLORS[node.type] || "#3b82f6";
        const customColor = nodeColors[node.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label } = node;
        const handleClick = onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(node.id, e.clientX, e.clientY); } : undefined;
        if (node.type === "decision") {
          return (
            <g key={node.id} data-testid={`workflow-node-${node.id}`} onClick={handleClick} style={onNodeClick ? { cursor: "pointer" } : {}}>
              <polygon points={`${x},${y - h / 2} ${x + w / 2},${y} ${x},${y + h / 2} ${x - w / 2},${y}`}
                fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
              <text x={x} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontFamily="Manrope">{label}</text>
            </g>
          );
        }
        return (
          <g key={node.id} data-testid={`workflow-node-${node.id}`} onClick={handleClick} style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={node.type === "start" || node.type === "end" ? h / 2 : 6}
              fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
            <text x={x} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontFamily="Manrope">{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function DAGSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, tasks, dependencies, width, height } = layout;
  const statusColors = { running: "#3b82f6", success: "#22c55e", failed: "#ef4444", pending: "#94a3b8", skipped: "#e2e8f0" };
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <EdgeArrowDefs />
      {title && <text x={width / 2} y={28} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {dependencies.map((dep, i) => {
        if (!dep.points || dep.points.length < 2) return null;
        const d = dep.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        return <path key={i} d={d} fill="none" stroke="#94a3b8" strokeWidth="1.5" markerEnd="url(#ai-arrow)" />;
      })}
      {tasks.map((task) => {
        const defaultColor = statusColors[task.status] || "#94a3b8";
        const customColor = nodeColors[task.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label } = task;
        return (
          <g key={task.id} data-testid={`dag-task-${task.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(task.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={6} fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
            <text x={x} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="500" fontFamily="Manrope">{label}</text>
            <circle cx={x - w / 2 + 10} cy={y} r={5} fill={color} />
          </g>
        );
      })}
    </svg>
  );
}

function PipelineSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, stages, flows, width, height } = layout;
  const stageTypeColors = { input: "#3b82f6", process: "#8b5cf6", model: "#22c55e", output: "#f59e0b", transform: "#06b6d4", validate: "#ef4444" };
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="pipe-arrow" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto">
          <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
        </marker>
      </defs>
      {title && <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {flows.map((flow, i) => (
        <g key={i}>
          <line x1={flow.fromX} y1={flow.fromY} x2={flow.toX} y2={flow.toY} stroke="#94a3b8" strokeWidth="2" markerEnd="url(#pipe-arrow)" />
          {flow.label && <text x={(flow.fromX + flow.toX) / 2} y={flow.fromY - 8} textAnchor="middle" fill="#94a3b8" fontSize="10" fontFamily="Manrope">{flow.label}</text>}
        </g>
      ))}
      {stages.map((stage, i) => {
        const defaultColor = stageTypeColors[stage.type] || COLORS[i % COLORS.length];
        const customColor = nodeColors[stage.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label } = stage;
        return (
          <g key={stage.id} data-testid={`pipeline-stage-${stage.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(stage.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x} y={y - h / 2} width={w} height={h} rx={8} fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
            <rect x={x} y={y - h / 2} width={w} height={6} rx={3} fill={color} />
            <text x={x + w / 2} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="Manrope">{label}</text>
            <text x={x + w / 2} y={y + 18} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="Manrope">{stage.type}</text>
          </g>
        );
      })}
    </svg>
  );
}

function KnowledgeGraphSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, entities, relations, width, height } = layout;
  const typeColors = { person: "#3b82f6", concept: "#8b5cf6", place: "#22c55e", event: "#f59e0b", entity: "#06b6d4", object: "#ec4899" };
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <EdgeArrowDefs />
      {title && <text x={width / 2} y={28} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {relations.map((rel, i) => {
        if (!rel.points || rel.points.length < 2) return null;
        const d = rel.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        const mid = rel.points[Math.floor(rel.points.length / 2)];
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#cbd5e1" strokeWidth="1.5" markerEnd="url(#ai-arrow)" />
            <rect x={mid.x - rel.relation.length * 3.5} y={mid.y - 10} width={rel.relation.length * 7} height={16} rx={8} fill="#f8fafc" stroke="#e2e8f0" strokeWidth="1" />
            <text x={mid.x} y={mid.y + 3} textAnchor="middle" fill="#64748b" fontSize="9" fontFamily="Manrope">{rel.relation}</text>
          </g>
        );
      })}
      {entities.map((entity) => {
        const defaultColor = typeColors[entity.type] || "#06b6d4";
        const customColor = nodeColors[entity.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label } = entity;
        return (
          <g key={entity.id} data-testid={`kg-entity-${entity.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(entity.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <circle cx={x} cy={y} r={Math.max(w, h) / 2} fill={color} opacity={0.15} stroke={color} strokeWidth="2" />
            <text x={x} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="Manrope">{label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function ModelArchSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, layers, connections, width, height } = layout;
  const layerTypeColors = { dense: "#3b82f6", conv: "#8b5cf6", pooling: "#06b6d4", dropout: "#f59e0b", activation: "#ec4899", embedding: "#ef4444", attention: "#f97316", input: "#22c55e", output: "#22c55e" };

  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <defs>
        <marker id="model-arrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0, 8 3, 0 6" fill="#94a3b8" />
        </marker>
      </defs>
      {title && <text x={width / 2} y={30} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {connections.map((conn, i) => (
        <line key={i} x1={conn.fromX} y1={conn.fromY} x2={conn.toX} y2={conn.toY} stroke="#cbd5e1" strokeWidth="2" markerEnd="url(#model-arrow)" />
      ))}
      {layers.map((layer, i) => {
        const defaultColor = layerTypeColors[layer.type] || "#3b82f6";
        const customColor = nodeColors[layer.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label, shape } = layer;
        return (
          <g key={layer.id} data-testid={`model-layer-${layer.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(layer.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={6} fill={color} opacity={0.15} stroke={color} strokeWidth="2.5" />
            <rect x={x - w / 2} y={y - h / 2} width={w} height={8} fill={color} rx={3} />
            <text x={x} y={y + 2} textAnchor="middle" fill="#0f172a" fontSize="12" fontWeight="600" fontFamily="Manrope">{label}</text>
            {shape && <text x={x} y={y + 16} textAnchor="middle" fill="#94a3b8" fontSize="9" fontFamily="JetBrains Mono">{shape}</text>}
          </g>
        );
      })}
    </svg>
  );
}

function SystemArchSVG({ layout, nodeColors = {}, onNodeClick }) {
  const { title, components, connections, width, height } = layout;
  const layerColorMap = { frontend: "#3b82f6", backend: "#22c55e", data: "#f59e0b", ai: "#8b5cf6", external: "#64748b" };
  return (
    <svg id="diagram-svg" viewBox={`0 0 ${width} ${height}`} width={width} height={height}>
      <EdgeArrowDefs />
      {title && <text x={width / 2} y={28} textAnchor="middle" fill="#0f172a" fontSize="16" fontWeight="600" fontFamily="Outfit">{title}</text>}
      {connections.map((conn, i) => {
        if (!conn.points || conn.points.length < 2) return null;
        const d = conn.points.reduce((a, p, j) => a + (j === 0 ? `M ${p.x} ${p.y}` : ` L ${p.x} ${p.y}`), "");
        const mid = conn.points[Math.floor(conn.points.length / 2)];
        return (
          <g key={i}>
            <path d={d} fill="none" stroke="#94a3b8" strokeWidth="1.5"
              markerEnd="url(#ai-arrow)"
              markerStart={conn.bidirectional ? "url(#ai-arrow-bi)" : "none"} />
            {conn.label && <text x={mid.x + 6} y={mid.y - 6} fill="#94a3b8" fontSize="10" fontFamily="Manrope">{conn.label}</text>}
          </g>
        );
      })}
      {components.map((comp) => {
        const defaultColor = layerColorMap[comp.layer] || comp.color || "#94a3b8";
        const customColor = nodeColors[comp.id];
        const color = customColor && customColor !== "transparent" ? customColor : defaultColor;
        const { x, y, width: w, height: h, label } = comp;
        return (
          <g key={comp.id} data-testid={`sysarch-comp-${comp.id}`}
            onClick={onNodeClick ? (e) => { e.stopPropagation(); onNodeClick(comp.id, e.clientX, e.clientY); } : undefined}
            style={onNodeClick ? { cursor: "pointer" } : {}}>
            <rect x={x - w / 2} y={y - h / 2} width={w} height={h} rx={8} fill={color} opacity={0.12} stroke={color} strokeWidth="2" />
            <rect x={x - w / 2} y={y - h / 2} width={w} height={6} rx={3} fill={color} opacity={0.7} />
            <text x={x} y={y + 4} textAnchor="middle" fill="#0f172a" fontSize="11" fontWeight="600" fontFamily="Manrope">{label}</text>
            {comp.layer && <text x={x} y={y + 18} textAnchor="middle" fill={color} fontSize="9" fontWeight="500" fontFamily="Manrope">{comp.layer}</text>}
          </g>
        );
      })}
    </svg>
  );
}

